/**
 * 会员现状 API（ziwei-knowledge /api/v1/membership，阶段二：me 下发白名单+水印档案；
 * 阶段三：套餐列表 + 下单/查单/confirm 购买闭环）
 * - GET /me：等级/到期/allow_capture/用户名昵称（Bearer 登录态）
 * - 未登录或拉取失败 → null（前端按"无白名单=默认保护"降级）
 */
import { KNOWLEDGE_BASE } from './userCases';

export interface MembershipMe {
  /** free/basic/pro（user_memberships 阶段三发放前恒 free） */
  level: string;
  expire_at: string | null;
  /** 截屏/复制白名单：true=放行（控制台维护） */
  allow_capture: boolean;
  username: string;
  nickname: string;
}

/** 当前登录用户的会员现状；无 token 或失败返回 null（调用方按缺省保护处理） */
export async function fetchMembershipMe(token: string | null): Promise<MembershipMe | null> {
  if (!token) return null;
  try {
    const res = await fetch(`${KNOWLEDGE_BASE}/api/v1/membership/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = (await res.json().catch(() => null)) as { code?: number; data?: MembershipMe; msg?: string } | null;
    if (!json || typeof json.code !== 'number' || json.code !== 0) return null;
    return json.data ?? null;
  } catch {
    return null;
  }
}

/**
 * 从 JWT payload 解出 user_id（水印用；不严格验签——仅取显示字段，
 * 真实鉴权在服务端 me 接口。解不出返回 null）
 */
export function jwtUserId(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(pad), (ch) => ch.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes)) as { user_id?: unknown };
    const uid = Number(payload.user_id);
    return Number.isFinite(uid) && uid > 0 ? uid : null;
  } catch {
    return null;
  }
}

// ---------- 阶段三：套餐列表 + 下单/查单/confirm ----------

/** 可购买套餐（服务端仅下发 enabled） */
export interface MemberPackage {
  id: number;
  level: string; // basic/pro
  name: string;
  duration_days: number; // 0=永久
  price: number;
  original_price: number | null;
  currency: string;
  enabled: boolean;
  sort_order: number;
}

/** 下单/查单/confirm 统一回体 */
export interface MemberOrder {
  order_no: string;
  amount: number;
  status: number; // 1待支付/2已支付/3已过期
  payment_url?: string;
  h5_url?: string;
  expire_time?: string;
  level?: string; // 已支付后回带最新等级
}

/** 支付渠道（沿用服务端枚举：1 微信 / 2 支付宝） */
export const PAY_CHANNEL_WECHAT = 1;
export const PAY_CHANNEL_ALIPAY = 2;

interface Env<T> {
  code: number;
  data: T;
  msg?: string;
}

/** 会员购买域请求：统一信封解包（code≠0 抛错，透出 msg） */
async function memberRequest<T>(path: string, token: string | null, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${KNOWLEDGE_BASE}/api/v1/membership${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    });
  } catch (e) {
    throw new Error(`网络请求失败：${e instanceof Error ? e.message : String(e)}`);
  }
  const env = (await res.json().catch(() => null)) as Env<T> | null;
  if (!env || typeof env.code !== 'number') throw new Error(`响应格式非法（HTTP ${res.status}）`);
  if (env.code !== 0) throw new Error(env.msg || `接口错误（code ${env.code}）`);
  return env.data;
}

/** 启用中的套餐列表 */
export function fetchMemberPackages(token: string | null): Promise<MemberPackage[]> {
  return memberRequest<{ items: MemberPackage[] }>('/packages', token).then((d) => d.items || []);
}

/** 下单（出码）：返回带 payment_url/h5_url 的待支付单 */
export function createMemberOrder(
  token: string | null,
  body: { membership_package_id: number; payment_method: number; client_type?: string },
): Promise<MemberOrder> {
  return memberRequest<MemberOrder>('/orders', token, { method: 'POST', body: JSON.stringify(body) });
}

/** 查单（待支付时服务端会自动同步 armor 并可能自动发放） */
export function getMemberOrder(token: string | null, orderNo: string): Promise<MemberOrder> {
  return memberRequest<MemberOrder>(`/orders/${encodeURIComponent(orderNo)}`, token);
}

/** 支付后发放确认（服务端核实 armor 已支付才发放） */
export function confirmMemberOrder(token: string | null, orderNo: string): Promise<MemberOrder> {
  return memberRequest<MemberOrder>(`/orders/${encodeURIComponent(orderNo)}/confirm`, token, { method: 'POST' });
}
